<script setup lang="ts">
import { Form } from 'vee-validate'
import { computed, reactive } from 'vue'
import { number } from 'yup'
import { numberErr } from '@/constants'

const props = defineProps<{
  fields: {
    type: string // "input" | "select" | "date" | "range" | "checkbox"; // TODO
    key: string
    title?: string
    label?: string
    placeholder?: string
    options?: { label: string; value: string }[]
    rangeKeys?: { min: string; max: string }
  }[]
}>()

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (e: 'apply', values: Record<string, any>): void
  (e: 'reset'): void
}>()

const validationSchema = {
  minRequest: number()
    .typeError(numberErr)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  maxRequest: number()
    .typeError(numberErr)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  minRate: number()
    .typeError(numberErr)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
  maxRate: number()
    .typeError(numberErr)
    .nullable()
    .transform((v, o) => (o === '' ? null : v)),
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const values = reactive<Record<string, any>>({})

props.fields.forEach((f) => {
  if (f.type === 'range' && f.rangeKeys) {
    values[f.rangeKeys.min] = ''
    values[f.rangeKeys.max] = ''
  } else if (f.type === 'checkbox') {
    values[f.key] = false
  } else {
    values[f.key] = ''
  }
})

const hasAnyValue = computed(() => {
  return Object.values(values).some((v) => {
    if (typeof v === 'boolean') return v === true
    return v !== '' && v !== null && v !== undefined
  })
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onSubmit = (formValues: Record<string, any>) => {
  const allValues = { ...values, ...formValues }
  const filteredValues = Object.fromEntries(
    Object.entries(allValues).filter(([, v]) => v !== null && v !== '' && v !== undefined)
  )
  emit('apply', filteredValues)
}
</script>

<template>
    <div>
        <div class="p-4 bg-white border border-[#d8d8d8] rounded-xl mt-5">
            <Form :validation-schema="validationSchema" @submit="onSubmit" class="space-y-4">
                <div class="flex flex-col lg:grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6 lg:gap-y-1">
                    <template v-for="field in fields" :key="field.key">

                        <div v-if="Object.keys(field).length === 0"></div>

                        <div v-else-if="field.type === 'input'" class="flex flex-col ">
                            <span class="text-[13px] font-medium text-[#1b325a] ms-2 mb-2">{{ field.title }}</span>
                            <Input :value="values[field.key]" @update:modelValue="(val: unknown) => values[field.key] = val"
                                :name="field.key" class="w-full" :label="field.label"
                                :placeholder="field.placeholder" />
                        </div>

                        <div v-else-if="field.type === 'range'" class="flex flex-col ">
                            <span class="text-[13px] font-medium text-[#1b325a] ms-2 mb-2">{{ field.title }}</span>
                            <div class="flex items-center gap-x-6">
                                <Input v-if="field.rangeKeys" :name="field.rangeKeys.min"
                                    v-model="values[field.rangeKeys.min]" class="w-full" label="از"
                                    :placeholder="field.placeholder" type="number" />
                                <Input v-if="field.rangeKeys" :name="field.rangeKeys.max"
                                    v-model="values[field.rangeKeys.max]" class="w-full" label="تا"
                                    :placeholder="field.placeholder" type="number" />
                            </div>
                        </div>

                        <div v-else-if="field.type === 'select'" class="flex flex-col ">
                            <span class="text-[13px] font-medium text-[#1b325a] ms-2 mb-2">{{ field.title }}</span>
                            <Select variant="checkbox" v-model="values[field.key]" :label="field.label"
                                :options="field.options" />
                        </div>

                        <div v-else-if="field.type === 'date'" class="flex flex-col  h-[98.96px]">
                            <span class="text-[13px] font-medium text-[#1b325a] ms-2 mb-2">{{ field.title }}</span>
                            <div v-if="field.rangeKeys" class="flex items-center gap-x-6">
                                <AlirezaDatePicker v-model="values[field.rangeKeys.min]" placeholder="از" />
                                <AlirezaDatePicker v-model="values[field.rangeKeys.max]" placeholder="تا" />
                            </div>
                        </div>


                        <div v-else-if="field.type === 'checkbox'" class=" flex ">
                            <label class="flex items-center gap-2 cursor-pointer select-none">
                                <input type="checkbox" v-model="values[field.key]" :name="field.key"
                                    class="hidden peer" />
                                <div
                                    class="w-5 h-5 rounded-md border-[2.5px] border-[#858585] peer-checked:border-[#2d5396] peer-checked:bg-[#2d5396] flex items-center justify-center duration-200">
                                    <svg v-if="values[field.key]" class="w-6 h-6 text-white" fill="none"
                                        stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                                        <path d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span class="flex-1 text-sm font-medium text-[#1b325a] text-right">
                                    {{ field.label }}
                                </span>
                            </label>
                        </div>

                    </template>
                </div>

                <!-- Actions -->
                <div class="flex items-center justify-end gap-1.5">
                    <Button type="submit" label="اعمال فیلتر" :isAdmin="true"
                        class="text-base font- h-12! px-5 rounded-[10px]! " :loading="false" :disabled="!hasAnyValue" />
                </div>
            </Form>
        </div>
    </div>
</template>
